import { IEventrFlight, SectionId } from "@eventr/common";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { useMemo } from "react";
import { routes } from "routing";
import { getCategoryProperties } from "utils";
import { useScreenTypeContext } from "../state/screenTypeProvider";
import { formatFlightNumber } from "../utils/flights";
import { FlightAirports } from "./flightComponents/flightAirports";
import { FlightBookingInfo } from "./flightComponents/flightBookingInfo";
import { FlightDate } from "./flightComponents/flightDate";
import { FlightHeader } from "./flightComponents/flightHeader";
import { FlightTimes } from "./flightComponents/flightTimes";
import { useMatchTagsAndUsers } from "./hooks/useMatchTagsAndUsers";
import { ObjectProvider } from "./providers/objectProvider";
import { BaseCard } from "./subComponents/baseCard";
interface FlightCardProps {
  id?: string;
  data?: IEventrFlight;
  past?: boolean;
  border?: boolean;
  date?: string | undefined;
}

export const FlightCard = ({ id, data, past, date }: FlightCardProps) => {
  const { screenType } = useScreenTypeContext();
  const { pushPath } = useGlobalRouter();
  const { matchTagsAndUser } = useMatchTagsAndUsers();

  const passenger = useMemo(() => {
    if ((data?.passengers_list?.length ?? 0) === 0) {
      return null;
    }
    return data?.passengers_list.find((passenger) => {
      if (passenger.users) {
        return passenger.users?.some((user) => {
          return matchTagsAndUser(user);
        });
      } else {
        return [];
      }
    });
  }, [data?.passengers_list, matchTagsAndUser]);

  const flightNumber = useMemo(() => {
    if (!data || !data?.bookref) return "";
    return formatFlightNumber(data.bookref);
  }, [data]);

  if (!data) return null;

  const attachments = {
    notes: data.notes,
    extra_docs: data.extra_docs,
    website: data.website,
  };

  const FlightAirport = (
    <FlightAirports
      origin={data.departure?.dep_name ?? data.departure_airport?.label}
      destination={data.arrival?.arr_name ?? data.arrival_airport?.label}
      originTerminal={data.departure?.dep_terminal}
      destinationTerminal={data.arrival?.arr_terminal}
    />
  );
  const FlightTimeSection = (
    <FlightTimes
      originAirportABR={data.outboundAirportAbr ?? data.departure_airport?.obj?.iata}
      destinationAirportABR={data.inboundAirportAbr ?? data.arrival_airport?.obj?.iata}
      destinationTime={data.arrival?.arr_time}
      originTime={data.departure?.dep_time}
    />
  );
  const FlightBookingSection = (
    <FlightBookingInfo
      attachments={attachments}
      passenger={passenger ?? undefined}
      passengers={data.passengers_list}
      chatNames={[data.bookref]}
      airline={data.airline}
    />
  );

  const onClick = () => {
    pushPath(routes.item.flight.build({ itemId: data._id }), { state: { screenType } });
  };

  return (
    <ObjectProvider past={past ?? false} _id={data._id} sectionID={SectionId.FLIGHT.toString()}>
      {screenType === "upNext" ? (
        <BaseCard id={id} onClick={onClick} past={past}>
          <FlightHeader
            category={getCategoryProperties(data?.categories?.[0])}
            label={data.label}
            flightNumber={flightNumber}
            labelTime={data.labelTime}
            visibility={data.visibility}
            departure={data.departure?.dep_time}
          />
          {FlightAirport}
          {FlightTimeSection}
          {FlightBookingSection}
        </BaseCard>
      ) : screenType === "edit" ? (
        <BaseCard id={id} past={past}>
          <FlightDate
            category={getCategoryProperties(data?.categories?.[0])}
            label={data.label ?? ""}
            date={data.flightDate}
            flightNumber={flightNumber}
            visibility={data.visibility}
          />
          {FlightAirport}
          {FlightTimeSection}
          {FlightBookingSection}
        </BaseCard>
      ) : screenType === "personal" ? (
        <BaseCard id={id} onClick={onClick} past={past}>
          <FlightDate
            category={getCategoryProperties(data?.categories?.[0])}
            label={data.label ?? ""}
            date={data.flightDate}
            flightNumber={flightNumber}
            visibility={data.visibility}
          />
          {FlightAirport}
          {FlightTimeSection}
          {FlightBookingSection}
        </BaseCard>
      ) : screenType === "team" || screenType === "teamSearch" || screenType === "teamMember" ? (
        <BaseCard id={id} onClick={onClick} past={past}>
          <FlightHeader
            category={getCategoryProperties(data?.categories?.[0])}
            label={data.label}
            flightNumber={flightNumber}
            labelTime={data.labelTime}
            visibility={data.visibility}
            departure={data.departure?.dep_time}
            arrival={data.arrival?.arr_time}
            checkIn={data.check_in}
            date={date}
          />
          {FlightAirport}
          {FlightTimeSection}
          <FlightBookingInfo attachments={attachments} passengers={data.passengers_list} chatNames={[data.bookref]} />
        </BaseCard>
      ) : null}
    </ObjectProvider>
  );
};
